/**
 * Download Buttons Component
 * 
 * This component provides buttons to download search results in different formats:
 * - CSV (Comma-Separated Values) - Simple, universal format
 * - Excel (.xlsx) - Formatted spreadsheet with multiple sheets
 * 
 * The component handles the download process including:
 * - Triggering the API call
 * - Creating a downloadable file
 * - Showing loading states
 * - Error handling
 */

import React, { useState } from 'react';
import { downloadResults, triggerDownload } from '../utils/api';

function DownloadButtons({ jobId, activeFilters = {} }) {
  // ═══════════════════════════════════════════════════════════════
  // STATE
  // ═══════════════════════════════════════════════════════════════
  
  // Loading state for each format
  const [loading, setLoading] = useState({
    csv: false,
    excel: false
  });

  // ═══════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════

  /**
   * Handle download button click
   * 
   * @param {string} format - 'csv' or 'excel'
   */
  const handleDownload = async (format) => {
    // Set loading state for this format
    setLoading(prev => ({ ...prev, [format]: true }));
    
    try {
      // Call the API to get the file
      const blob = await downloadResults(jobId, format, activeFilters);
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const extension = format === 'csv' ? 'csv' : 'xlsx';
      const filename = `leads_${timestamp}.${extension}`;
      
      // Trigger the download
      triggerDownload(blob, filename);
      
    } catch (error) {
      console.error(`Download ${format} failed:`, error);
      alert(`Failed to download ${format.toUpperCase()}. Please try again.`);
    } finally {
      // Clear loading state
      setLoading(prev => ({ ...prev, [format]: false }));
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <div className="flex items-center space-x-2">
      {/* CSV Download Button */}
      <button
        onClick={() => handleDownload('csv')}
        disabled={loading.csv || loading.excel}
        className="btn-secondary flex items-center space-x-2 py-2 px-4 text-sm disabled:opacity-50"
        title="Download as CSV file"
      >
        {loading.csv ? (
          <div className="w-4 h-4 border-2 border-gray-400/30 border-t-gray-400 rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
        <span>CSV</span>
      </button>

      {/* Excel Download Button */}
      <button
        onClick={() => handleDownload('excel')}
        disabled={loading.csv || loading.excel}
        className="btn-primary flex items-center space-x-2 py-2 px-4 text-sm disabled:opacity-50"
        title="Download as Excel file"
      >
        {loading.excel ? (
          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        )}
        <span>Excel</span>
      </button>
    </div>
  );
}

export default DownloadButtons;
